import agentWebSocket from "@app/config/agentWebSocket";
import { useClearAgentSession, useUploadSessionDocument } from "@app/queries/agent";
import type { AgentChatMode } from "@app/state/types";
import { useAuth0 } from "@auth0/auth0-react";
import type { AgentSessionDocument } from "@globals";
import { useChatAttachments } from "@lib/agent/useChatAttachments";
import { confirm } from "@lib/common/components/modals/modals";
import AgentChat from "@lib/experiment/agent/AgentChat/AgentChat";
import { useAgentChatContext } from "@lib/experiment/agent/AgentChatContext";
import ChatSessionUserFeedback from "@lib/experiment/agent/ChatSessionUserFeedback/ChatSessionUserFeedback";
import { notifications } from "@lib/notification/notification";
import { ActionIcon, Text, Tooltip } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

export type ExperimentAgentChatVariant = "draft" | "experiment";

const REACTION_DESIGN_PROMPT = `Please extract structured reaction-design metadata from this experiment's name and description and persist it.

For the experiment you have in context, infer:
- reaction: {type, scheme, transformation, conditions_summary} — based on the experiment name/description.
- components: any of {substrates, coupling_partners, catalysts, ligands, bases, solvents, additives, products}, each as an array of {name, role}.

Then call the experiment-update tool (PATCH /experiments/{experiment_id}) with the body field reaction_design_context set to the structure above. Only include component categories that have at least one realistic compound; omit empty ones. Do not invent compounds if the input is too vague — leave categories empty in that case.

Confirm in your reply what you wrote.`;

type ExperimentAgentChatProps = {
  chatMode: AgentChatMode;
  setChatMode: (mode: AgentChatMode) => void;
  toggle: (value: boolean) => void;
  variant?: ExperimentAgentChatVariant;
};

export default function ExperimentAgentChat({
  chatMode,
  toggle,
  setChatMode,
  variant = "experiment"
}: ExperimentAgentChatProps) {
  const {
    experimentId,
    teamId,
    messages,
    isStreaming,
    connectionState,
    send,
    enterResearchMode,
    exitResearchMode,
    isResearchMode,
    stop,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    allowReseed,
    hasRestoredHistory,
    agentSessionId,
    committedMessageCount
  } = useAgentChatContext();
  const clearMutation = useClearAgentSession();
  // Owned here (not in AgentChat) so a confirmed clear can drop the compose-area chips in
  // lockstep with the server-side session/document cascade.
  const attachments = useChatAttachments(experimentId);
  // Stable useCallbacks; aliased so `handleClear` can depend on just the methods rather than
  // the whole `attachments` object (which is a fresh literal every render and would otherwise
  // cascade re-renders down the AgentChat tree).
  const resetAttachments = attachments.reset;
  const abortPendingUploads = attachments.abortPendingUploads;
  const uploadMutation = useUploadSessionDocument();
  // `mutateAsync` is referentially stable across renders (unlike the mutation object itself),
  // so the draft bootstrap effect can depend on just this without re-registering every render.
  const uploadDocument = uploadMutation.mutateAsync;
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuerySentRef = useRef(false);
  const isDraft = variant === "draft";

  // Files picked on the landing page (InspirationalChatBox) ride along via React Router
  // history state — File objects survive structured clone. The agent session doesn't exist
  // there, so they couldn't be uploaded yet; we upload them here once the draft (and its
  // session) exists. The upload attaches them to the session, so the agent picks them up
  // server-side on the first turn — nothing about the documents travels on the WS prompt.
  const initialFilesRef = useRef<File[]>(
    (() => {
      if (!isDraft) {
        return [];
      }
      const state = location.state as { files?: unknown } | null;
      if (!state?.files || !Array.isArray(state.files)) {
        return [];
      }
      return state.files.filter((f): f is File => f instanceof File);
    })()
  );

  // Draft entry only: forward `?query=...` (and any handed-off files) as the first user
  // message, then strip the query from the URL/history so reload/back-nav doesn't resend.
  // Wait for the socket *and* the history fetch to resolve before deciding — otherwise a failed
  // history fetch would burn the one-shot flag and lose the query param across a retry. Also wait
  // for any in-flight stream, since `send` no-ops while streaming and the prompt would be dropped.
  useEffect(() => {
    if (!isDraft) {
      return;
    }
    if (
      connectionState !== "connected" ||
      isHistoryLoading ||
      historyError ||
      isStreaming ||
      initialQuerySentRef.current
    ) {
      return;
    }
    initialQuerySentRef.current = true;
    if (hasRestoredHistory) {
      return;
    }

    const initialQuery = searchParams.get("query");
    if (!initialQuery) {
      return;
    }

    const files = initialFilesRef.current;
    initialFilesRef.current = [];

    // The upload window can be seconds (large files), and `initialQuerySentRef` only guards this
    // mount. If the component remounts mid-upload (StrictMode, a route/key change), a fresh mount
    // would re-run this bootstrap while *this* mount's async is still in flight — sending the same
    // first prompt twice. Aborting on cleanup cancels the in-flight upload and skips the send, so
    // only the surviving mount dispatches the query.
    const controller = new AbortController();
    let sent = false;
    void (async () => {
      // Upload handed-off files first so they're attached to the session before the first turn —
      // the agent receives the session's document set server-side, no handles travel with the
      // prompt. A failed upload shouldn't block the message; the user can re-attach in-chat.
      const uploaded = await Promise.all(
        files.map(async file => {
          const formData = new FormData();
          formData.append("file", file);
          try {
            const document: AgentSessionDocument = await uploadDocument({
              params: { path: { experiment_id: experimentId } },
              body: formData,
              signal: controller.signal
            });
            return document.filename;
          } catch {
            // An abort isn't a user-facing failure — the chip/turn is being torn down anyway.
            if (!controller.signal.aborted) {
              notifications.error(`Failed to attach ${file.name}`);
            }
            return null;
          }
        })
      );
      if (controller.signal.aborted) {
        return;
      }
      const filenames = uploaded.filter((name): name is string => name !== null);
      send(initialQuery, { filenames });
      sent = true;

      // Strip `?query=` only after the prompt has actually been dispatched. Stripping it up front
      // would lose the message if the user reloads during the (possibly slow) file-upload window;
      // keeping it until send means a reload in that window simply retries.
      const next = new URLSearchParams(searchParams);
      next.delete("query");
      void navigate(
        { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
        { replace: true }
      );
    })();

    return () => {
      controller.abort();
      // Release the one-shot claim if we tore down before dispatching. The ref persists across a
      // StrictMode remount (and a genuine remount makes a fresh one), so without this the surviving
      // mount would see the claim still set and never send the query.
      if (!sent) {
        initialQuerySentRef.current = false;
      }
    };
  }, [
    isDraft,
    connectionState,
    isHistoryLoading,
    historyError,
    isStreaming,
    hasRestoredHistory,
    searchParams,
    send,
    navigate,
    location.pathname,
    experimentId,
    uploadDocument
  ]);

  const handleClear = useCallback(async () => {
    // Optimistic: clear the UI immediately. The DELETE drops the DB row + cascade
    // messages; closing the WS forces the consumer's `disconnect` handler to evict
    // the cached Strands Agent so the next prompt rebuilds fresh state. We then
    // reconnect so the chat is usable again without a manual reload.
    clearLocal();
    // Abort in-flight uploads BEFORE the DELETE so a late POST can't recreate the session.
    // Uploaded chips are kept until the DELETE succeeds: if it fails, their documents still
    // exist on the session, so the chips must survive the rollback with them.
    abortPendingUploads();
    agentWebSocket.close();
    try {
      await clearMutation.mutateAsync({ params: { path: { experiment_id: experimentId } } });
      // The session's documents are gone with the cascade; now the chips are stale, drop them.
      resetAttachments();
    } catch {
      // Notification is raised by the mutation's onError. Re-open the seed gate so the
      // refetched history rehydrates the cleared UI back to the original conversation.
      allowReseed();
      void refetchHistory();
    }
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
  }, [
    clearLocal,
    abortPendingUploads,
    resetAttachments,
    clearMutation,
    experimentId,
    teamId,
    allowReseed,
    refetchHistory,
    getAccessTokenSilently
  ]);

  const onClear = useCallback(() => {
    const subject = isDraft ? "draft" : "experiment";
    confirm({
      title: "Clear conversation",
      danger: true,
      labels: { confirm: "Clear" },
      children: <Text size="sm">This permanently removes the chat history for this {subject}.</Text>,
      onConfirm: () => void handleClear()
    });
  }, [handleClear, isDraft]);

  const clearDisabled = isStreaming || isHistoryLoading || clearMutation.isPending || messages.length === 0;

  function onGenerateReactionDesign() {
    toggle(true);
    send(REACTION_DESIGN_PROMPT);
  }

  const reactionDesignAction = isDraft ? (
    <Tooltip
      label="Generate reaction details with AI from this experiment's name and description. Fills in Overview, Components, and Safety. You can edit them afterward."
      multiline
      w={280}
      position="bottom"
      withArrow
    >
      <ActionIcon
        variant="subtle"
        color="violet"
        onClick={onGenerateReactionDesign}
        disabled={isStreaming || connectionState !== "connected"}
        aria-label="Generate reaction details with AI"
      >
        <IconSparkles size={18} />
      </ActionIcon>
    </Tooltip>
  ) : null;

  const userFeedback = agentSessionId ? (
    <ChatSessionUserFeedback
      agentSessionId={agentSessionId}
      disabled={isStreaming}
      messageCount={committedMessageCount}
    />
  ) : null;

  return (
    <AgentChat
      attachments={attachments}
      messages={messages}
      isStreaming={isStreaming}
      connectionState={connectionState}
      send={send}
      enterResearchMode={enterResearchMode}
      exitResearchMode={exitResearchMode}
      isResearchMode={isResearchMode}
      stop={stop}
      isHistoryLoading={isHistoryLoading}
      historyError={historyError}
      onRetryHistory={() => void refetchHistory()}
      onClear={onClear}
      disabledClear={clearDisabled}
      userFeedback={userFeedback}
      headerActions={reactionDesignAction}
      variant={variant}
      toggle={toggle}
      chatMode={chatMode}
      setChatMode={setChatMode}
    />
  );
}
